import { createApp } from 'vue';
import { createPinia } from 'pinia';
import App from './App.vue';
import router from './router';
import '@/assets/styles/main.scss';
import { FontAwesomeIcon } from './plugins/fontawesome.js';
import { registerSW } from 'virtual:pwa-register';
import { useAuthStore } from '@/stores/useAuthStore';
import { redirectToLogin } from '@/utils/authRedirect';
import { startIdleLogout } from '@/utils/idleLogout';
import { ensureI18nMessagesLoaded } from '@/composables/useI18n';
import { logPerf, perfNow } from '@/utils/perf';
import { initInstallPromptCapture } from '@/utils/installPrompt';
import { recoverFromChunkLoadError } from '@/utils/chunkLoadRecovery';
import { hardResetAndReload } from '@/utils/hardReset';
import {
  getCurrentVersion,
  getStoredVersion,
  isNewVersionAvailable,
  isUpdatePending,
  setStoredVersion,
  setUpdatePending
} from '@/utils/versionManager';
import { isPushNotificationClickMessage, resolvePushNavigationTarget } from '@/utils/pushNavigation.mjs';

const logInfo = (message) => {
  console.info(`mentalLoad: ${message}`);
};

const logWarn = (message) => {
  console.warn(`mentalLoad: ${message}`);
};

const logError = (message, error) => {
  console.error(`mentalLoad: ${message}`, error);
};

const initializeVersionState = () => {
  const currentVersion = getCurrentVersion();
  const storedVersion = getStoredVersion();

  if (!storedVersion) {
    setStoredVersion(currentVersion);
  }

  const updatePending = isNewVersionAvailable();
  setUpdatePending(updatePending);

  if (!updatePending) {
    setStoredVersion(currentVersion);
  }

  console.log('[VERSION]', {
    current: currentVersion,
    stored: getStoredVersion(),
    updatePending
  });
};

const handleChunkError = async (error) => {
  const recovered = await recoverFromChunkLoadError(error, 'window');
  if (!recovered) {
    logWarn('Chunk-Fehler erneut aufgetreten. Update wird beim nächsten sicheren Einstieg angewendet.');
    setUpdatePending(true);
  }
};

const handleVisibilitySafeUpdate = () => {
  if (document.visibilityState !== 'visible') return;
  if (!isUpdatePending()) return;

  console.info('[UPDATE] Silent update on resume');
  void hardResetAndReload();
};

initializeVersionState();
initInstallPromptCapture();
registerSW({
  immediate: true,
  onRegisteredSW() {
    logInfo('Service Worker registriert.');
  },
  onNeedRefresh() {
    logInfo('Service Worker Update erkannt (silent).');
    setUpdatePending(true);
  },
  onRegisterError(error) {
    logError('Service Worker-Registrierung fehlgeschlagen.', error);
  }
});

if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    void handleChunkError(event.error || new Error(event.message));
  });
  window.addEventListener('unhandledrejection', (event) => {
    void handleChunkError(event.reason);
  });
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', handleVisibilitySafeUpdate);
}

const handleServiceWorkerNotificationClick = async (payload) => {
  if (!isPushNotificationClickMessage(payload)) return;
  const target = resolvePushNavigationTarget(payload.payload);
  try {
    await router.replace({ path: target.path, query: target.query });
  } catch (error) {
    logError('Push-Navigation fehlgeschlagen.', error);
  }
};

if (typeof navigator !== 'undefined' && navigator.serviceWorker) {
  navigator.serviceWorker.addEventListener('message', (event) => {
    void handleServiceWorkerNotificationClick(event.data);
  });
}

const appStartAt = perfNow();

const app = createApp(App);
app.component('Fa', FontAwesomeIcon);
const pinia = createPinia();
app.use(pinia);
app.use(router);
app.mount('#app');

router.isReady().then(() => {
  logPerf('AppStart', perfNow() - appStartAt);
  ensureI18nMessagesLoaded().catch((error) => {
    logWarn('I18n-Nachladen fehlgeschlagen.');
    logError('I18n-Nachladen fehlgeschlagen.', error);
  });
});

const auth = useAuthStore(pinia);

const idleManager = startIdleLogout({
  idleMs: 60 * 60 * 1000,
  onIdle: () => {
    if (!auth.token) return;
    auth.logout();
    if (isUpdatePending()) {
      console.info('[UPDATE] Silent update on session expiry');
      void hardResetAndReload();
      return;
    }
    redirectToLogin({ reason: 'idle' });
  }
});

router.afterEach(() => {
  idleManager?.notifyActivity();
});
